import { MapPin, TriangleAlert } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  formatCurrency,
  orEmptyValue,
  toPlaceMarker,
} from "@/lib/hcbb-cost-estimator-utils";
import { HcbbDetailAddress } from "@/pages/components/hcbb-cost-estimator/hcbb-address-line";
import { HcbbPhoneAction } from "@/pages/components/hcbb-cost-estimator/hcbb-phone-button";
import { HcbbInfoHint } from "@/pages/components/hcbb-cost-estimator/hcbb-info-hint";
import type {
  HcbbFacilityResult,
  HcbbProfessionalResult,
} from "@/model/hcbb-cost-estimator";
import type { PlaceMarker } from "@/model/place";

/**
 * Facilities and providers come back as different payloads but read as the same
 * card, so both funnel through one component. The two exported wrappers below
 * are the only place either payload shape is known.
 */
type HcbbResultCardProps = {
  title: string;
  badges: CardBadge[];
  /** The practice a provider works out of; facilities have none. */
  subtitle?: string;
  address: string;
  displayRadius: string;
  estimatedCost: number;
  phone: string;
  actualCost: number;
  /** Null when the row has no usable coordinates, which disables the map. */
  mapMarker: PlaceMarker | null;
  detailsLabel: string;
  onViewOnMap: (marker: PlaceMarker) => void;
  onViewSpendSummary: () => void;
  onViewDisclaimer: () => void;
  onViewDetails: () => void;
};

type CardBadge = {
  label: string;
  tone: "network" | "neutral";
};

/**
 * HCBB has no per-row flag for either caveat yet, so both apply to every
 * result. Kept as data so wiring them up later is a payload change, not a
 * markup change.
 */
const CARE_CAVEATS = [
  "Prior Authorization may be required",
  "Visit limitation may exist",
];

const BADGE_TONES: Record<CardBadge["tone"], string> = {
  network: "bg-green-600 text-white",
  neutral: "bg-gray-300 text-gray-800",
};

const HcbbResultCard = ({
  title,
  badges,
  subtitle,
  address,
  displayRadius,
  estimatedCost,
  phone,
  actualCost,
  mapMarker,
  detailsLabel,
  onViewOnMap,
  onViewSpendSummary,
  onViewDisclaimer,
  onViewDetails,
}: HcbbResultCardProps) => (
  <article className="border-1 border-gray rounded-lg border shadow-md">
    <header className="flex items-center justify-between gap-2 p-2">
      <h3 className="font-medium">{title}</h3>
      <div className="flex shrink-0 gap-2">
        {badges
          // A missing network or specialty renders nothing rather than "NA".
          .filter((badge) => Boolean(badge.label) && badge.label !== "NA")
          .map((badge) => (
            <Badge
              key={badge.label}
              className={cn("rounded-full font-light", BADGE_TONES[badge.tone])}
            >
              {badge.label}
            </Badge>
          ))}
      </div>
    </header>
    <hr />

    <div className="flex gap-4 p-4">
      <div className="flex-1 space-y-4">
        {subtitle && (
          <h4 className="text-medium font-bold text-gray-800">
            {orEmptyValue(subtitle)}
          </h4>
        )}

        <HcbbDetailAddress address={address} displayRadius={displayRadius} />

        <ul className="flex gap-6">
          {CARE_CAVEATS.map((caveat) => (
            <li
              key={caveat}
              className="flex items-center gap-1 text-sm text-gray-800"
            >
              <TriangleAlert
                aria-hidden="true"
                height={14}
                className="shrink-0 text-yellow-600"
              />
              {caveat}
            </li>
          ))}
        </ul>
      </div>

      {/* Not `flex-1`: the cost column shrinks to its content so the address
          side takes the slack. */}
      <div className="space-y-2">
        <div className="flex items-center justify-end gap-1">
          <p className="text-right text-sm font-medium text-gray-700">
            Your Total Estimated Cost{" "}
            <span className="text-large font-bold text-primary">
              {formatCurrency(estimatedCost)}
            </span>
          </p>

          {/* Named for what it opens, not for the icon: "info" on 50
              rows tells a screen-reader user nothing about which row
              they are on. */}
          <HcbbInfoHint
            label={`Read the cost estimate disclaimer for ${title}`}
            onClick={onViewDisclaimer}
          >
            Click to view full disclaimer.
          </HcbbInfoHint>
        </div>

        <p className="text-right text-sm font-medium text-gray-700">
          Contracted Amount:{" "}
          <span className="pr-2 text-right text-medium font-bold text-primary">
            {formatCurrency(actualCost)}
          </span>
        </p>

        <DeductibleNotice onView={onViewSpendSummary} />

        {/* The map icon carries its target in the label — "map" alone says
            nothing about which of 50 results it opens. Disabled rather than
            hidden when the row has no coordinates, so the row does not
            change shape between results. */}
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            disabled={!mapMarker}
            onClick={mapMarker ? () => onViewOnMap(mapMarker) : undefined}
            aria-label={
              mapMarker
                ? `Show ${title} on a map`
                : `No map location on file for ${title}`
            }
          >
            <MapPin aria-hidden="true" />
          </Button>

          <HcbbPhoneAction name={title} phone={phone} />

          {/* Named for its row, like the map control beside it: "View
              Provider Details" repeated down a list of fifty says nothing
              about which provider. */}
          <Button
            variant="outline"
            onClick={onViewDetails}
            aria-label={`${detailsLabel} for ${title}`}
          >
            {detailsLabel}
          </Button>
        </div>
      </div>
    </div>
  </article>
);

/**
 * The deductible line under each estimate. "View" opens the spend summary in
 * the slide-over instead of leaving the results behind, so the member can
 * check the deductible against the estimate they were just looking at.
 */
const DeductibleNotice = ({ onView }: { onView: () => void }) => (
  <p className="text-right text-sm font-medium text-gray-700">
    You have not met the deductible limit.
    <button
      type="button"
      onClick={onView}
      className="px-2 py-0 text-primary underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      View
    </button>
  </p>
);

export const HcbbFacilityCard = ({
  facility,
  onViewOnMap,
  onViewSpendSummary,
  onViewDisclaimer,
  onViewDetails,
}: {
  facility: HcbbFacilityResult;
  onViewOnMap: (marker: PlaceMarker) => void;
  onViewSpendSummary: () => void;
  onViewDisclaimer: () => void;
  onViewDetails: () => void;
}) => (
  <HcbbResultCard
    title={facility.displayName}
    badges={[
      {
        label: `${facility.networkName}${facility.tier ? ` - ${facility.tier}` : ""}`,
        tone: "network",
      },
    ]}
    address={facility.address}
    displayRadius={facility.displayRadius}
    estimatedCost={facility.estimatedCost}
    phone={facility.phone}
    actualCost={facility.actualCost}
    mapMarker={toPlaceMarker({
      name: facility.displayName,
      address: facility.address,
      displayRadius: facility.displayRadius,
      latitude: facility.latitude,
      longitude: facility.longitude,
    })}
    detailsLabel="View Facility Details"
    onViewOnMap={onViewOnMap}
    onViewSpendSummary={onViewSpendSummary}
    onViewDisclaimer={onViewDisclaimer}
    onViewDetails={onViewDetails}
  />
);

export const HcbbProviderCard = ({
  professional,
  onViewOnMap,
  onViewSpendSummary,
  onViewDisclaimer,
  onViewDetails,
}: {
  professional: HcbbProfessionalResult;
  onViewOnMap: (marker: PlaceMarker) => void;
  onViewSpendSummary: () => void;
  onViewDisclaimer: () => void;
  onViewDetails: () => void;
}) => (
  <HcbbResultCard
    title={professional.displayName}
    badges={[
      { label: professional.specialty, tone: "neutral" },
      {
        label: `${professional.networkName}${professional.tier ? ` - ${professional.tier}` : ""}`,
        tone: "network",
      },
    ]}
    subtitle={professional.facilityName}
    address={professional.address}
    displayRadius={professional.displayRadius}
    estimatedCost={professional.estimatedCost}
    phone={professional.phone}
    actualCost={professional.actualCost}
    // Always null against today's payload — every professional comes back with
    // "NA" coordinates, and their `facilityName` matches nothing in the
    // facilities list, so there is nothing to borrow a position from. Built
    // from the fields anyway so the button lights up the day HCBB sends them.
    mapMarker={toPlaceMarker({
      name: professional.displayName,
      address: professional.address,
      displayRadius: professional.displayRadius,
      latitude: professional.latitude,
      longitude: professional.longitude,
    })}
    detailsLabel="View Provider Details"
    onViewOnMap={onViewOnMap}
    onViewSpendSummary={onViewSpendSummary}
    onViewDisclaimer={onViewDisclaimer}
    onViewDetails={onViewDetails}
  />
);
